import { useState, TouchEvent } from "react";

/**
 * Type definition for swipe direction
 * @property {string | null} direction - The direction of the swipe (left, right, or null)
 */
export type UseSwipeFn = {
  /**
   * The direction of the swipe (left, right, or null)
   */
  direction: string | null;
};

/**
 * Custom hook for detecting swipe gestures on touch devices.
 *
 * Features:
 * - Detects left and right swipe gestures
 * - Configurable minimum swipe distance threshold
 * - Provides touch event handlers for easy integration
 * - Supports optional callback function for swipe events
 * - Tracks current swipe direction state
 * - Handles touch start, move, and end events
 * - Prevents false positives from regular touch events
 *
 * @param func - Optional callback function called when a swipe is detected
 * @returns Object containing touch event handlers and current swipe direction
 */
export const useSwipe = (
  func?: (direction: UseSwipeFn["direction"]) => void,
) => {
  const [direction, setDirection] = useState<null | string>(null);
  const [touchStart, setTouchStart] = useState<null | number>(null);
  const [touchEnd, setTouchEnd] = useState<null | number>(null);
  const minSwipeDistance = 50;
  const onTouchMove = (e: TouchEvent<HTMLElement>) =>
    setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = (_e: TouchEvent<HTMLElement>) => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    let dir = null;
    if (isLeftSwipe) dir = "left";
    if (isRightSwipe) dir = "right";

    func?.(dir);
    setDirection(dir);
  };

  const onTouchStart = (e: TouchEvent<HTMLElement>) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  return { onTouchMove, onTouchEnd, onTouchStart, direction };
};
